import { useState, useMemo } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  TableCellsIcon,
  UserIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  XCircleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { useCustomers } from '../hooks/useCustomers';
import CustomerForm from '../components/CustomerForm';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../components/ui/BrandModal';
import {
  Customer,
  CreateCustomerDto,
  UpdateCustomerDto,
  CustomerType,
  CustomerStatus,
  PreferredContact,
  getCustomerStatusColor,
  getCustomerTypeColor,
  getPreferredContactLabel,
  formatCustomerType,
  formatCustomerStatus,
} from '../api/customers';
import { safeNumber } from '../utils/formatters';
import PageHero from '../components/ui/PageHero';
import EmptyState from '../components/ui/EmptyState';

interface CustomerFilters {
  type?: CustomerType;
  status?: CustomerStatus;
  city?: string;
  province?: string;
  preferredContact?: PreferredContact;
}

export default function Customers() {
  const {
    customers,
    statistics,
    loading,
    error,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    refreshData,
  } = useCustomers();

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<CustomerFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [sortBy] = useState<string>('name');
  const [sortOrder] = useState<'asc' | 'desc'>('asc');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // Filter and search logic
  const filteredCustomers = useMemo(() => {
    let result = customers;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (customer) =>
          customer.name.toLowerCase().includes(term) ||
          customer.code.toLowerCase().includes(term) ||
          customer.firstName?.toLowerCase().includes(term) ||
          customer.lastName?.toLowerCase().includes(term) ||
          customer.contactPerson?.toLowerCase().includes(term) ||
          customer.email?.toLowerCase().includes(term) ||
          customer.city?.toLowerCase().includes(term) ||
          customer.province?.toLowerCase().includes(term)
      );
    }

    // Apply filters
    if (filters.type) {
      result = result.filter((customer) => customer.type === filters.type);
    }
    if (filters.status) {
      result = result.filter((customer) => customer.status === filters.status);
    }
    if (filters.city) {
      result = result.filter((customer) => customer.city === filters.city);
    }
    if (filters.province) {
      result = result.filter((customer) => customer.province === filters.province);
    }
    if (filters.preferredContact) {
      result = result.filter((customer) => customer.preferredContact === filters.preferredContact);
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal: any = a[sortBy as keyof Customer];
      let bVal: any = b[sortBy as keyof Customer];
      
      if (aVal === undefined && bVal === undefined) return 0;
      if (aVal === undefined) return sortOrder === 'asc' ? 1 : -1;
      if (bVal === undefined) return sortOrder === 'asc' ? -1 : 1;
      
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [customers, searchTerm, filters, sortBy, sortOrder]);

  // Pagination logic
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const paginatedCustomers = filteredCustomers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Get unique values for filter options
  const uniqueCities = [...new Set(customers.map((customer) => customer.city).filter(Boolean))];
  const uniqueProvinces = [...new Set(customers.map((customer) => customer.province).filter(Boolean))];

  // Event handlers
  const handleCreateCustomer = async (data: CreateCustomerDto | UpdateCustomerDto) => {
    const result = await createCustomer(data as CreateCustomerDto);
    if (result) {
      setShowAddModal(false);
    }
  };

  const handleUpdateCustomer = async (data: CreateCustomerDto | UpdateCustomerDto) => {
    if (selectedCustomer) {
      const result = await updateCustomer(selectedCustomer.id, data as UpdateCustomerDto);
      if (result) {
        setShowEditModal(false);
        setSelectedCustomer(null);
      }
    }
  };

  const handleDeleteCustomer = async () => {
    if (selectedCustomer) {
      const success = await deleteCustomer(selectedCustomer.id);
      if (success) {
        setShowDeleteModal(false);
        setSelectedCustomer(null);
      }
    }
  };

  const clearFilters = () => {
    setFilters({});
  };

  const openViewModal = (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowViewModal(true);
  };

  const openEditModal = (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowEditModal(true);
  };

  const openDeleteModal = (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowDeleteModal(true);
  };

  const getStatusBadge = (customer: Customer) => {
    const color = getCustomerStatusColor(customer.status);
    const statusText = formatCustomerStatus(customer.status);
    
    const colorClasses = {
      green: 'bg-emerald-100 text-emerald-800',
      yellow: 'bg-amber-100 text-amber-800',
      red: 'bg-rose-100 text-rose-800',
      gray: 'bg-violet-100 text-violet-800',
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClasses[color as keyof typeof colorClasses]}`}>
        {statusText}
      </span>
    );
  };

  const getTypeBadge = (customer: Customer) => {
    const color = getCustomerTypeColor(customer.type);
    const typeText = formatCustomerType(customer.type);
    
    const colorClasses = {
      blue: 'bg-cyan-100 text-cyan-800',
      purple: 'bg-purple-100 text-purple-800',
      green: 'bg-emerald-100 text-emerald-800',
      orange: 'bg-orange-100 text-orange-800',
      indigo: 'bg-cyan-100 text-cyan-800',
      red: 'bg-rose-100 text-rose-800',
      yellow: 'bg-amber-100 text-amber-800',
      gray: 'bg-violet-100 text-violet-800',
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClasses[color as keyof typeof colorClasses]}`}>
        {typeText}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHero
        title="Customers"
        subtitle="Manage your customer database and track relationships"
        actions={
          <button type="button" onClick={() => setShowAddModal(true)} className="btn btn-primary focus-pop">
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Customer
          </button>
        }
      />

      {/* Statistics Overview */}
      {statistics && (
        <div className="mt-8 space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center">
                <UserIcon className="h-5 w-5 text-cyan-600 mr-2" />
                <h2 className="text-lg font-semibold text-slate-900">Customer Analytics</h2>
              </div>
            </div>
            
            <div className="p-6">
              {/* Primary Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-cyan-50 p-6 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-cyan-600 mb-1">Total Customers</p>
                      <p className="text-3xl font-bold text-blue-900">{safeNumber(statistics.totalCustomers)}</p>
                      <p className="text-sm text-cyan-700 mt-1">Registered customers</p>
                    </div>
                    <div className="bg-blue-100 p-3 rounded-full">
                      <UserIcon className="h-8 w-8 text-cyan-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600 mb-1">Total Revenue</p>
                      <p className="text-3xl font-bold text-green-900">
                        R{safeNumber(statistics.totalRevenue).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                      </p>
                      <p className="text-sm text-green-700 mt-1">Customer lifetime value</p>
                    </div>
                    <div className="bg-green-100 p-3 rounded-full">
                      <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                  <div className="flex items-center">
                    <div className="bg-green-100 p-2 rounded-lg mr-3">
                      <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-500">Active</p>
                      <p className="text-xl font-semibold text-green-600">{safeNumber(statistics.activeCustomers)}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                  <div className="flex items-center">
                    <div className="bg-gray-100 p-2 rounded-lg mr-3">
                      <XCircleIcon className="h-4 w-4 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-500">Inactive</p>
                      <p className="text-xl font-semibold text-slate-600">{safeNumber(statistics.inactiveCustomers)}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                  <div className="flex items-center">
                    <div className="bg-yellow-100 p-2 rounded-lg mr-3">
                      <ClockIcon className="h-4 w-4 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-500">Potential</p>
                      <p className="text-xl font-semibold text-yellow-600">{safeNumber(statistics.potentialCustomers)}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                  <div className="flex items-center">
                    <div className="bg-red-100 p-2 rounded-lg mr-3">
                      <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-500">Suspended</p>
                      <p className="text-xl font-semibold text-red-600">{safeNumber(statistics.suspendedCustomers)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filter Controls */}
      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex-1 max-w-lg">
          <div className="flex items-center w-full rounded-md ring-1 ring-inset ring-gray-300 bg-white focus-within:ring-2 focus-within:ring-inset focus-within:ring-cyan-500">
            <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
              <MagnifyingGlassIcon className="h-5 w-5" />
            </span>
            <input
              type="text"
              name="search"
              id="search"
              className="flex-1 min-w-0 py-1.5 pr-3 border-0 bg-transparent text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0 sm:text-sm sm:leading-6"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Filter Toggle */}
          <button
            type="button"
            className={`relative inline-flex items-center px-3 py-2 text-sm font-semibold ${
              showFilters
                ? 'bg-cyan-600 text-white'
                : 'bg-white text-slate-900 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80'
            } rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-600`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
            {Object.keys(filters).length > 0 && (
              <span className="ml-1 inline-flex items-center rounded-full bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700">
                {Object.keys(filters).length}
              </span>
            )}
          </button>

          {/* View Mode Toggle */}
          <div className="flex rounded-md shadow-sm">
            <button
              type="button"
              className={`relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-10 ${
                viewMode === 'grid' ? 'bg-cyan-600 text-white' : 'bg-white text-slate-900'
              }`}
              onClick={() => setViewMode('grid')}
            >
              <Squares2X2Icon className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={`relative -ml-px inline-flex items-center rounded-r-md px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-10 ${
                viewMode === 'table' ? 'bg-cyan-600 text-white' : 'bg-white text-slate-900'
              }`}
              onClick={() => setViewMode('table')}
            >
              <TableCellsIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="mt-4 bg-slate-50/80 p-4 rounded-lg">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div>
              <label className="block text-sm font-medium text-slate-700">Type</label>
              <select
                value={filters.type || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value as CustomerType || undefined }))}
                className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              >
                <option key="empty-option-1753461297363" value="">All Types</option>
                {Object.values(CustomerType).map((type) => (
                  <option key={type} value={type}>
                    {formatCustomerType(type)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Status</label>
              <select
                value={filters.status || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as CustomerStatus || undefined }))}
                className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              >
                <option key="empty-option-1753461297363" value="">All Statuses</option>
                {Object.values(CustomerStatus).map((status) => (
                  <option key={status} value={status}>
                    {formatCustomerStatus(status)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">City</label>
              <select
                value={filters.city || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value || undefined }))}
                className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              >
                <option key="empty-option-1753461297363" value="">All Cities</option>
                {uniqueCities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Province</label>
              <select
                value={filters.province || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, province: e.target.value || undefined }))}
                className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              >
                <option key="empty-option-1753461297363" value="">All Provinces</option>
                {uniqueProvinces.map((province) => (
                  <option key={province} value={province}>
                    {province}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Preferred Contact</label>
              <select
                value={filters.preferredContact || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, preferredContact: e.target.value as PreferredContact || undefined }))}
                className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              >
                <option key="empty-option-1753461297363" value="">All Contacts</option>
                {Object.values(PreferredContact).map((contact) => (
                  <option key={contact} value={contact}>
                    {getPreferredContactLabel(contact)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {Object.keys(filters).length > 0 && (
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
              >
                <XMarkIcon className="-ml-1 mr-1 h-4 w-4" />
                Clear Filters
              </button>
            </div>
          )}
        </div>
      )}

      {/* Customer List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {paginatedCustomers.map((customer) => (
            <div
              key={customer.id}
              className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow duration-200"
            >
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      {customer.isBusinessCustomer ? (
                        <BuildingOfficeIcon className="h-8 w-8 text-slate-400" />
                      ) : (
                        <UserIcon className="h-8 w-8 text-slate-400" />
                      )}
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-slate-900 truncate">
                        {customer.fullName}
                      </h3>
                      <p className="text-sm text-slate-500">{customer.code}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    {getStatusBadge(customer)}
                  </div>
                </div>

                <div className="mt-4">
                  <div className="flex items-center justify-between">
                    {getTypeBadge(customer)}
                    <span className="text-sm text-slate-500">
                      {getPreferredContactLabel(customer.preferredContact)}
                    </span>
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  {customer.email && (
                    <div className="flex items-center text-sm text-slate-500">
                      <EnvelopeIcon className="h-4 w-4 mr-2" />
                      <span className="truncate">{customer.email}</span>
                    </div>
                  )}
                  {customer.phone && (
                    <div className="flex items-center text-sm text-slate-500">
                      <PhoneIcon className="h-4 w-4 mr-2" />
                      <span>{customer.phone}</span>
                    </div>
                  )}
                  {customer.city && (
                    <div className="flex items-center text-sm text-slate-500">
                      <MapPinIcon className="h-4 w-4 mr-2" />
                      <span>{customer.city}, {customer.province}</span>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex items-center justify-between">
                  <div className="text-sm">
                    <span className="text-slate-500">Balance: </span>
                    <span className={`font-medium ${customer.currentBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      R{customer.currentBalance.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openViewModal(customer)}
                      className="text-cyan-600 hover:text-indigo-900 text-sm font-medium"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openEditModal(customer)}
                      className="text-cyan-600 hover:text-indigo-900 text-sm font-medium"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openDeleteModal(customer)}
                      className="text-red-600 hover:text-red-900 text-sm font-medium"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Table View - Full Implementation */
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-slate-50/80">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Customer
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Type & Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Contact Info
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Location
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Balance
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Last Activity
                </th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedCustomers.map((customer) => (
                <tr key={customer.id} className="hover:bg-slate-50/80">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-cyan-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-cyan-800">
                            {customer.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-slate-900">
                          {customer.name}
                        </div>
                        <div className="text-sm text-slate-500">
                          {customer.code}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-1">
                      {getTypeBadge(customer)}
                      {getStatusBadge(customer)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-slate-900">
                      {customer.email && (
                        <div className="flex items-center mb-1">
                          <EnvelopeIcon className="h-4 w-4 text-slate-400 mr-1" />
                          {customer.email}
                        </div>
                      )}
                      {customer.phone && (
                        <div className="flex items-center">
                          <PhoneIcon className="h-4 w-4 text-slate-400 mr-1" />
                          {customer.phone}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    <div>
                      {customer.city && customer.province ? 
                        `${customer.city}, ${customer.province}` : 
                        customer.city || customer.province || 'Not specified'
                      }
                    </div>
                    {customer.country && customer.country !== 'South Africa' && (
                      <div className="text-xs text-slate-400">{customer.country}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-medium ${
                      customer.currentBalance > 0 
                        ? 'text-green-600' 
                        : customer.currentBalance < 0 
                          ? 'text-red-600' 
                          : 'text-slate-900'
                    }`}>
                      R{customer.currentBalance.toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    {customer.updatedAt ? new Date(customer.updatedAt).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => openViewModal(customer)}
                        className="text-cyan-600 hover:text-indigo-900"
                        title="View Details"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => openEditModal(customer)}
                        className="text-amber-600 hover:text-amber-900"
                        title="Edit Customer"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => openDeleteModal(customer)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Customer"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {paginatedCustomers.length === 0 && (
            <EmptyState
              className="mt-0"
              title="No customers found"
              description={
                filteredCustomers.length === 0
                  ? 'Get started by creating your first customer.'
                  : 'Try adjusting your search or filter criteria.'
              }
              icon={<UserGroupIcon className="h-6 w-6" />}
              action={
                filteredCustomers.length === 0 ? (
                  <button type="button" onClick={() => setShowAddModal(true)} className="btn btn-primary text-sm focus-pop">
                    <PlusIcon className="h-4 w-4" />
                    Add Customer
                  </button>
                ) : undefined
              }
            />
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-lg shadow">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-slate-700">
                Showing{' '}
                <span className="font-medium">
                  {(currentPage - 1) * itemsPerPage + 1}
                </span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, filteredCustomers.length)}
                </span>{' '}
                of <span className="font-medium">{filteredCustomers.length}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      page === currentPage
                        ? 'z-10 bg-cyan-50 border-cyan-500 text-cyan-600'
                        : 'bg-white border-gray-300 text-slate-500 hover:bg-slate-50/80'
                    }`}
                  >
                    {page}
                  </button>
                ))}

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Add Customer Modal */}
      <BrandModal open={showAddModal} onClose={() => setShowAddModal(false)} maxWidthClass="max-w-4xl">
        <BrandModalHeader
          title="Add New Customer"
          subtitle="Fill in the customer details to add them to your database."
          onClose={() => setShowAddModal(false)}
        />
        <BrandModalBody>
          <CustomerForm
            onSubmit={handleCreateCustomer}
            onCancel={() => setShowAddModal(false)}
          />
        </BrandModalBody>
      </BrandModal>

      {/* Edit Customer Modal */}
      <BrandModal
        open={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedCustomer(null);
        }}
        maxWidthClass="max-w-4xl"
      >
        <BrandModalHeader
          title="Edit Customer"
          subtitle="Update customer information and settings."
          onClose={() => {
            setShowEditModal(false);
            setSelectedCustomer(null);
          }}
        />
        <BrandModalBody>
          {selectedCustomer && (
            <CustomerForm
              customer={selectedCustomer}
              onSubmit={handleUpdateCustomer}
              onCancel={() => {
                setShowEditModal(false);
                setSelectedCustomer(null);
              }}
            />
          )}
        </BrandModalBody>
      </BrandModal>

      {/* Delete Customer Modal */}
      <BrandModal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedCustomer(null);
        }}
        maxWidthClass="max-w-lg"
      >
        <BrandModalHeader
          title="Delete Customer"
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedCustomer(null);
          }}
        />
        <BrandModalBody>
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
          </div>
          <p className="text-sm text-slate-600 text-center">
            Are you sure you want to delete {selectedCustomer?.fullName}? This action cannot be undone.
          </p>
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={() => {
              setShowDeleteModal(false);
              setSelectedCustomer(null);
            }}
          >
            Cancel
          </button>
          <button type="button" className="btn btn-danger text-sm" onClick={handleDeleteCustomer}>
            Delete
          </button>
        </BrandModalFooter>
      </BrandModal>
    </div>
  );
} 